/**
 * ENG-8540: the one place that knows how deep a channel config is nested.
 *
 * `GET /agents/:codeName/channel-configs/:channelId` returns the whole
 * `agent_channel_configs` ROW wrapped in a `config` key, and that row has its
 * own `config` JSONB column:
 *
 *   { config: { id, agent_id, team_id, channel_id, status, config: { …payload… } } }
 *              ^^^^^^ the row                              ^^^^^^ the payload
 *
 * Every CLI reader had independently written `data.config.<field>`, which is one
 * level short. That produced `undefined` for every field, so `slack status`
 * printed `-` across the board and `beam status` reported "No Beam identity
 * configured" for agents that had one.
 *
 * Two things made it worse than a cosmetic bug:
 *
 *   1. `data.config.team_id` DOES resolve — to the row's tenant team UUID. So
 *      the Slack table printed a real-looking wrong value in the Team ID cell
 *      rather than an obviously-empty one.
 *   2. `beam setup`'s "you already have an identity, regenerate?" prompt was
 *      gated on `existing.config?.beam_id`, so it was permanently false and the
 *      confirmation never appeared. The shallow read is precisely what made the
 *      keypair overwrite silent.
 *
 * Hence a shared helper rather than four corrected call sites: the next reader
 * gets the nesting right by construction, and `channel-config-envelope.test.ts`
 * pins this shape against the route so the two cannot drift apart again.
 */

/** The row as the API returns it, with its nested payload. */
export interface ChannelConfigRowEnvelope<TPayload = Record<string, unknown>> {
  config: ({
    id?: string;
    agent_id?: string;
    /** The TENANT team uuid - NOT any channel's team id. See note 1 above. */
    team_id?: string;
    channel_id?: string;
    status?: string;
    created_at?: string;
    updated_at?: string;
    /** The channel payload. This is the thing callers actually want. */
    config?: TPayload | null;
  }) | null;
}

export interface UnwrappedChannelConfig<TPayload> {
  /** The channel payload, or null when the agent has no config for this channel. */
  payload: TPayload | null;
  /** The ROW's status column (`configured` / `pending` / …), not the payload's. */
  status: string | null;
  /** True when a row exists at all, even if its payload is empty. */
  exists: boolean;
}

/**
 * Unwrap the two-level envelope into the payload the caller wants.
 *
 * Tolerates a null row, a row with a null payload, and a body that is missing
 * `config` entirely, because every one of those is a legitimate "not configured"
 * rather than an error.
 */
export function unwrapChannelConfig<TPayload = Record<string, unknown>>(
  body: ChannelConfigRowEnvelope<TPayload> | null | undefined,
): UnwrappedChannelConfig<TPayload> {
  const row = body?.config ?? null;
  if (!row) return { payload: null, status: null, exists: false };
  return {
    payload: (row.config ?? null) as TPayload | null,
    status: row.status ?? null,
    exists: true,
  };
}
